/**
 * URL search-param helpers for `/test/rbs` and `/test/setup` so a configuration
 * can be bookmarked or shared. Unrelated params are left untouched.
 */

export const JOB_IDS_SEARCH_PARAM = 'jobIds';
export const COUNTRY_SEARCH_PARAM = 'country';
/**
 * Deliberately NOT `prforceGroups`: that name is reserved by the platform's proctor
 * middleware, which reads it from the query *in place of* the `prforceGroups` cookie and
 * re-persists it into that cookie on the first request it handles. Opening a shared
 * `/test/rbs?prforceGroups=…` or `/test/setup?prforceGroups=…` link would therefore force
 * only the panel's buckets for the whole session and drop groups the panel never emits
 * (notably `auto_apply_jsf_migration_tst`, which trips JSF's killswitch). This namespaced
 * param carries the panel state for sharing only; the panel's buckets still go out as a
 * real `prforceGroups` value on generated links (and the RBS orchestrator draft POST).
 */
export const PRFORCE_GROUPS_SEARCH_PARAM = 'panelPrforceGroups';

/**
 * Parse the `jobIds` search param into a list of IDs.
 * `null` means the param is absent (caller should keep country defaults).
 * An empty / whitespace value yields `[]`.
 */
export const parseJobIdsSearchParam = (value: string | null): string[] | undefined => {
    if (value === null) return undefined;
    return value
        .split(/[,\n]/)
        .map((id) => id.trim())
        .filter(Boolean);
};

/** Serialize the Job IDs textarea (one ID per line) into a comma-separated search param. */
export const serializeJobIdsSearchParam = (jobIdsText: string): string =>
    jobIdsText
        .split('\n')
        .map((id) => id.trim())
        .filter(Boolean)
        .join(',');

/**
 * Copy `current` and apply `updates`. `null` deletes the key; any string
 * (including `''`) is written so an empty `jobIds` / `from` / `panelPrforceGroups`
 * stays distinguishable from a missing param (defaults).
 */
export const upsertSearchParams = (
    current: URLSearchParams,
    updates: Record<string, string | null>,
): URLSearchParams => {
    const next = new URLSearchParams(current);
    Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
    });
    return next;
};
